import { CreditCard, Check, Clock, X, RotateCcw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useTransactions } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

const statusConfig: Record<string, { icon: typeof Check; className: string; label: string }> = {
  pending: { icon: Clock, className: "bg-yellow-500/10 text-yellow-500", label: "Ожидает" },
  completed: { icon: Check, className: "bg-primary/10 text-primary", label: "Оплачено" },
  failed: { icon: X, className: "bg-destructive/10 text-destructive", label: "Ошибка" },
  refunded: { icon: RotateCcw, className: "bg-muted text-muted-foreground", label: "Возврат" },
};

export default function Transactions() {
  const { data: transactions, isLoading } = useTransactions();

  const totalRevenue = transactions
    ?.filter((t) => t.status === 'completed')
    .reduce((sum, t) => sum + t.price_rub, 0) || 0;

  return (
    <AdminLayout title="Транзакции" description="История покупок хитов">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Всего транзакций</CardDescription>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">{transactions?.length || 0}</span>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Успешных оплат</CardDescription>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">
              {transactions?.filter((t) => t.status === 'completed').length || 0}
            </span>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Общий доход</CardDescription>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold text-primary">₽{totalRevenue.toLocaleString()}</span>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            История транзакций
          </CardTitle>
          <CardDescription>Последние 100 транзакций</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !transactions?.length ? (
            <p className="text-muted-foreground text-center py-8">
              Пока нет транзакций
            </p>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => {
                const status = statusConfig[tx.status];
                const StatusIcon = status.icon;
                return (
                  <div key={tx.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {tx.telegram_users?.username 
                            ? `@${tx.telegram_users.username}` 
                            : tx.telegram_users?.first_name || 'Пользователь'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {tx.package_name} • {format(new Date(tx.created_at), "d MMM yyyy, HH:mm", { locale: ru })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">₽{tx.price_rub}</span>
                      <Badge className={status.className}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
